import type { ComponentType, SVGProps } from 'react';
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { CALLOUT_TONES, normalizeCalloutTone, type CalloutTone } from './Callout';

interface ToneStyle {
    readonly box: string;
    readonly icon: string;
    readonly swatch: string;
    readonly label: string;
    readonly Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

// Tailwind classes live here rather than in an app stylesheet: the rich-editor
// CSS is duplicated across apps we do not own, and literal tokens in this file
// are picked up by every consumer's Tailwind scanner.
const TONE_STYLES: Record<CalloutTone, ToneStyle> = {
    info: {
        box: 'border-blue-200 bg-blue-50 text-blue-900',
        icon: 'text-blue-500',
        swatch: 'bg-blue-400',
        label: 'Info',
        Icon: InformationCircleIcon
    },
    warning: {
        box: 'border-amber-200 bg-amber-50 text-amber-900',
        icon: 'text-amber-500',
        swatch: 'bg-amber-400',
        label: 'Warning',
        Icon: ExclamationTriangleIcon
    },
    success: {
        box: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        icon: 'text-emerald-500',
        swatch: 'bg-emerald-400',
        label: 'Success',
        Icon: CheckCircleIcon
    },
    danger: {
        box: 'border-red-200 bg-red-50 text-red-900',
        icon: 'text-red-500',
        swatch: 'bg-red-400',
        label: 'Danger',
        Icon: ExclamationCircleIcon
    }
};

interface ToneSwitcherProps {
    readonly tone: CalloutTone;
    readonly onPick: (tone: CalloutTone) => void;
}

function ToneSwitcher({ tone, onPick }: ToneSwitcherProps) {
    return (
        <div
            contentEditable={false}
            className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded-full bg-white/80 px-1 py-0.5 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-within:opacity-100"
        >
            {CALLOUT_TONES.map((candidate) => (
                <button
                    key={candidate}
                    type="button"
                    title={`${TONE_STYLES[candidate].label} tone`}
                    aria-label={`${TONE_STYLES[candidate].label} tone`}
                    aria-pressed={candidate === tone}
                    // Keeps the caret where it is: a mousedown inside the node
                    // view would otherwise move the ProseMirror selection.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onPick(candidate)}
                    className={
                        'h-3.5 w-3.5 rounded-full ring-offset-1 transition-transform hover:scale-110 ' +
                        TONE_STYLES[candidate].swatch +
                        (candidate === tone ? ' ring-2 ring-gray-500' : '')
                    }
                />
            ))}
        </div>
    );
}

/** Read-only render is the tinted box alone; editing adds the tone swatches. */
export function CalloutView({ node, editor, updateAttributes }: NodeViewProps) {
    const tone = normalizeCalloutTone(node.attrs.tone);
    const style = TONE_STYLES[tone];
    const { Icon } = style;

    return (
        <NodeViewWrapper className="my-3">
            <div className={`group relative flex gap-3 rounded-xl border p-3 ${style.box}`}>
                <span contentEditable={false} className="flex-shrink-0 select-none">
                    <Icon className={`mt-0.5 h-5 w-5 ${style.icon}`} aria-hidden="true" />
                </span>
                <NodeViewContent className="min-w-0 flex-1" />
                {editor.isEditable && (
                    <ToneSwitcher tone={tone} onPick={(next) => updateAttributes({ tone: next })} />
                )}
            </div>
        </NodeViewWrapper>
    );
}
